/**
 * Smart Affiliate Product Suggestions for Parent Dashboard
 * Shows personalized Amazon affiliate product recommendations
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Box,
  Chip,
  IconButton,
  Link,
  Alert
} from '@mui/material';
import {
  ShoppingCart,
  Refresh,
  TrendingUp
} from '@mui/icons-material';
import { getAmazonSearchUrl, AMAZON_DISCLOSURE } from '../utils/amazonHelper';

// Top 50 popular Amazon products with smart categorization
const POPULAR_PRODUCTS = [
  // Electronics & Tech
  { name: 'Nintendo Switch', category: 'Electronics', price: '$299', keywords: ['gaming', 'console', 'nintendo'], tags: ['gift', 'entertainment'] },
  { name: 'AirPods Pro', category: 'Electronics', price: '$199', keywords: ['audio', 'apple', 'wireless'], tags: ['gift', 'tech'] },
  { name: 'Fire TV Stick 4K', category: 'Electronics', price: '$39', keywords: ['streaming', 'tv', 'amazon'], tags: ['entertainment', 'home'] },
  { name: 'Echo Dot', category: 'Electronics', price: '$29', keywords: ['smart', 'speaker', 'alexa'], tags: ['smart home', 'convenience'] },
  { name: 'iPad', category: 'Electronics', price: '$329', keywords: ['tablet', 'apple', 'education'], tags: ['education', 'productivity'] },
  
  // Toys & Games
  { name: 'LEGO Classic Creative Set', category: 'Toys', price: '$39', keywords: ['lego', 'building', 'creative'], tags: ['education', 'creative'] },
  { name: 'Barbie Dreamhouse', category: 'Toys', price: '$179', keywords: ['barbie', 'dollhouse', 'imagination'], tags: ['imaginative play', 'gift'] },
  { name: 'Nerf Blaster Set', category: 'Toys', price: '$24', keywords: ['nerf', 'outdoor', 'active'], tags: ['outdoor', 'active play'] },
  { name: 'Hot Wheels Track Set', category: 'Toys', price: '$34', keywords: ['hot wheels', 'cars', 'racing'], tags: ['racing', 'collectible'] },
  { name: 'Play-Doh Variety Pack', category: 'Toys', price: '$12', keywords: ['play-doh', 'creative', 'art'], tags: ['creative', 'artistic'] },
  
  // Books & Education
  { name: 'Harry Potter Book Set', category: 'Books', price: '$52', keywords: ['harry potter', 'reading', 'fantasy'], tags: ['reading', 'imagination'] },
  { name: 'Magic Tree House Series', category: 'Books', price: '$28', keywords: ['adventure', 'reading', 'education'], tags: ['education', 'adventure'] },
  { name: 'National Geographic Kids', category: 'Books', price: '$14', keywords: ['science', 'education', 'facts'], tags: ['education', 'science'] },
  { name: 'Dr. Seuss Collection', category: 'Books', price: '$35', keywords: ['dr seuss', 'rhyming', 'classic'], tags: ['classic', 'reading'] },
  
  // Sports & Outdoor
  { name: 'Soccer Ball', category: 'Sports', price: '$15', keywords: ['soccer', 'sports', 'outdoor'], tags: ['sports', 'outdoor'] },
  { name: 'Basketball', category: 'Sports', price: '$18', keywords: ['basketball', 'sports', 'active'], tags: ['sports', 'team play'] },
  { name: 'Jump Rope', category: 'Sports', price: '$8', keywords: ['jump rope', 'exercise', 'active'], tags: ['exercise', 'coordination'] },
  { name: 'Bike Helmet', category: 'Sports', price: '$25', keywords: ['safety', 'biking', 'protection'], tags: ['safety', 'outdoor'] },
  
  // Home & Kitchen
  { name: 'Instant Pot', category: 'Kitchen', price: '$79', keywords: ['cooking', 'pressure cooker', 'meal prep'], tags: ['cooking', 'convenience'] },
  { name: 'Air Fryer', category: 'Kitchen', price: '$89', keywords: ['cooking', 'healthy', 'frying'], tags: ['healthy cooking', 'convenience'] },
  { name: 'Coffee Maker', category: 'Kitchen', price: '$45', keywords: ['coffee', 'morning', 'beverage'], tags: ['morning routine', 'beverage'] },
  { name: 'Water Bottle Set', category: 'Kitchen', price: '$22', keywords: ['hydration', 'health', 'portable'], tags: ['health', 'portable'] },
  
  // Clothing & Accessories
  { name: 'Kids Backpack', category: 'Clothing', price: '$24', keywords: ['school', 'backpack', 'organization'], tags: ['school', 'organization'] },
  { name: 'Rain Jacket', category: 'Clothing', price: '$35', keywords: ['weather', 'outdoor', 'protection'], tags: ['weather protection', 'outdoor'] },
  { name: 'Running Shoes', category: 'Clothing', price: '$65', keywords: ['shoes', 'exercise', 'comfort'], tags: ['exercise', 'comfort'] },
  
  // Impulse Buys & Small Items
  { name: 'Phone Case', category: 'Accessories', price: '$12', keywords: ['phone', 'protection', 'accessory'], tags: ['protection', 'accessory'] },
  { name: 'Screen Protector', category: 'Accessories', price: '$8', keywords: ['screen', 'protection', 'tech'], tags: ['protection', 'tech'] },
  { name: 'Car Phone Mount', category: 'Accessories', price: '$15', keywords: ['car', 'phone', 'mount'], tags: ['car', 'convenience'] },
  { name: 'Bluetooth Earbuds', category: 'Electronics', price: '$25', keywords: ['audio', 'wireless', 'budget'], tags: ['audio', 'budget'] },
  { name: 'Power Bank', category: 'Electronics', price: '$19', keywords: ['charging', 'portable', 'battery'], tags: ['charging', 'portable'] },
  { name: 'USB Cable Set', category: 'Electronics', price: '$12', keywords: ['cable', 'charging', 'tech'], tags: ['charging', 'tech'] },
  { name: 'Desk Organizer', category: 'Office', price: '$18', keywords: ['organization', 'desk', 'productivity'], tags: ['organization', 'productivity'] },
  { name: 'LED Strip Lights', category: 'Home', price: '$16', keywords: ['lighting', 'decor', 'ambient'], tags: ['decor', 'ambient'] },
  { name: 'Essential Oil Diffuser', category: 'Home', price: '$28', keywords: ['aromatherapy', 'relaxation', 'wellness'], tags: ['wellness', 'relaxation'] },
  { name: 'Yoga Mat', category: 'Fitness', price: '$22', keywords: ['yoga', 'exercise', 'fitness'], tags: ['fitness', 'wellness'] },
  { name: 'Waterproof Speaker', category: 'Electronics', price: '$35', keywords: ['speaker', 'waterproof', 'outdoor'], tags: ['outdoor', 'audio'] },
  { name: 'Travel Mug', category: 'Kitchen', price: '$14', keywords: ['travel', 'coffee', 'portable'], tags: ['travel', 'beverage'] },
  { name: 'Garden Tool Set', category: 'Garden', price: '$32', keywords: ['gardening', 'tools', 'outdoor'], tags: ['gardening', 'outdoor'] },
  { name: 'Pet Supplies Kit', category: 'Pet', price: '$45', keywords: ['pet', 'supplies', 'care'], tags: ['pet care', 'supplies'] },
  { name: 'Art Supplies Set', category: 'Arts', price: '$28', keywords: ['art', 'creative', 'crafts'], tags: ['creative', 'artistic'] },
  { name: 'Board Game Collection', category: 'Games', price: '$42', keywords: ['board game', 'family', 'entertainment'], tags: ['family time', 'entertainment'] },
  { name: 'Cooking Utensil Set', category: 'Kitchen', price: '$38', keywords: ['cooking', 'utensils', 'kitchen'], tags: ['cooking', 'kitchen'] },
  { name: 'Storage Bins Set', category: 'Home', price: '$25', keywords: ['storage', 'organization', 'home'], tags: ['organization', 'home'] },
  { name: 'First Aid Kit', category: 'Health', price: '$19', keywords: ['first aid', 'safety', 'emergency'], tags: ['safety', 'emergency'] },
  { name: 'Sunscreen Set', category: 'Health', price: '$16', keywords: ['sunscreen', 'protection', 'outdoor'], tags: ['protection', 'outdoor'] },
  { name: 'Vitamins & Supplements', category: 'Health', price: '$24', keywords: ['vitamins', 'health', 'wellness'], tags: ['health', 'wellness'] },
  { name: 'Cleaning Supplies Kit', category: 'Home', price: '$32', keywords: ['cleaning', 'home', 'maintenance'], tags: ['cleaning', 'maintenance'] },
  { name: 'Bedding Set', category: 'Home', price: '$48', keywords: ['bedding', 'bedroom', 'comfort'], tags: ['comfort', 'bedroom'] },
  { name: 'Towel Set', category: 'Home', price: '$22', keywords: ['towels', 'bathroom', 'hygiene'], tags: ['hygiene', 'bathroom'] }
];

function AffiliateSuggestions({ userPreferences = {}, familyData = {} }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Generate smart suggestions based on user preferences and family data
  const generateSmartSuggestions = () => {
    setLoading(true);
    
    // Simulate AI-powered recommendation logic
    setTimeout(() => {
      const smartSuggestions = selectSmartProducts(userPreferences, familyData);
      setSuggestions(smartSuggestions);
      setLoading(false);
    }, 1000);
  };

  // Smart product selection algorithm
  const selectSmartProducts = (preferences, family) => {
    const scoredProducts = POPULAR_PRODUCTS.map(product => {
      let score = 0;
      
      // Base score (random for variety)
      score += Math.random() * 10;
      
      // Category preferences
      if (preferences.favoriteCategories?.includes(product.category)) {
        score += 15;
      }
      
      // Price preferences
      const price = parseFloat(product.price.replace('$', ''));
      if (preferences.priceRange === 'low' && price < 30) score += 10;
      if (preferences.priceRange === 'medium' && price >= 30 && price <= 100) score += 10;
      if (preferences.priceRange === 'high' && price > 100) score += 10;
      
      // Family size considerations
      if (family && family.memberCount > 3 && product.tags.includes('family')) {
        score += 8;
      }
      
      // Seasonal considerations (mock)
      const month = new Date().getMonth();
      if (month >= 10 && month <= 12 && product.tags.includes('gift')) score += 12; // Holiday season
      if (month >= 5 && month <= 8 && product.tags.includes('outdoor')) score += 10; // Summer
      
      // Recent purchases (mock - would come from shopping history)
      if (product.category === 'Electronics' && preferences.recentPurchases?.includes('electronics')) {
        score -= 5; // Avoid too many electronics
      }
      
      return { ...product, score };
    });
    
    // Sort by score and return top 5
    return scoredProducts
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  };

  useEffect(() => {
    generateSmartSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferences, familyData]);

  const handleRefresh = () => {
    generateSmartSuggestions();
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp color="primary" />
            Smart Shopping Suggestions
          </Typography>
          <IconButton onClick={handleRefresh} disabled={loading} sx={{ minWidth: 48, minHeight: 48 }}>
            <Refresh />
          </IconButton>
        </Box>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          {AMAZON_DISCLOSURE}
        </Alert>

        {loading ? (
          <Typography>Generating personalized suggestions...</Typography>
        ) : (
          <Grid container spacing={2}>
            {suggestions.map((product, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      {product.category}
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {product.name}
                    </Typography>
                    <Typography variant="h5" color="success.main" gutterBottom>
                      {product.price}
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      {product.tags.slice(0, 2).map(tag => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                    
                    <Link
                      href={getAmazonSearchUrl(product.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textDecoration: 'none' }}
                    >
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<ShoppingCart />}
                        sx={{ mt: 'auto' }}
                      >
                        View on Amazon
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleRefresh}
            startIcon={<Refresh />}
            disabled={loading}
          >
            Get New Suggestions
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default AffiliateSuggestions;
